import Phaser from 'phaser';
import { t } from '@i18n/index';
import {
  PALETTE,
  TEXT_COLOUR,
  titleStyle,
  readoutStyle,
  labelStyle,
  FONT,
  px,
  motionMs,
  getReducedMotion,
} from '../theme';
import { layoutOf, watchResize } from '../ui/layout';
import { createScrollArea, type ScrollArea } from '../ui/scrollArea';
import { verdictKey } from '@core/campaign/toll';
import { HARD_SLOTS, type ClueSlot } from '@core/mystery/types';
import { createButton } from '../ui/button';
import { createListRow } from '../ui/panel';
import type { Campaign, Verdict } from '@core/campaign/campaign';
import { MYSTERY_IDS, type MysteryId } from '@core/mystery/types';
import { clearSave } from '../saveGame';
import { addBackdrop, FINALE_BACKDROP } from '../assets';
import { audio } from '../audio/engine';
import { DRONE_ROOT } from '../audio/motifs';

/**
 * Der Abschlussbericht — und das Ende des Falls.
 *
 * Zwei Phasen. Erst die Wahl: Der Spieler vertritt eine These, vor einem
 * Untersuchungsausschuss, der weiss, wie vollstaendig die Akte ist. Dann die
 * Aufloesung, inszeniert statt aufgelistet: Zeile fuer Zeile, mit Pausen,
 * Klang und einem eigenen Ende fuer jede der vier Kombinationen aus „richtig“
 * und „belegt“.
 *
 * Frueher stand alles auf einmal da, auf einem Bildschirm. Das Ende eines
 * Spiels, das eine Stunde lang auf diesen Moment hinarbeitet, braucht Zeit —
 * die Aufloesung soll man erleben, nicht ablesen. Ein Tippen zeigt trotzdem
 * jederzeit alles auf einmal, und wer reduzierte Bewegung eingestellt hat,
 * bekommt es ohne Inszenierung.
 */

/** Ein Element der Aufloesung und wann es erscheint (Sekunden ab Beginn). */
interface Beat {
  readonly object: Phaser.GameObjects.GameObject & { alpha: number; y: number; height?: number };
  readonly at: number;
  readonly onShow?: () => void;
}

export class FinaleScene extends Phaser.Scene {
  private campaign!: Campaign;
  private choice: MysteryId | null = null;
  private verdict: Verdict | null = null;
  private scroll: ScrollArea | null = null;
  /** Wie weit die Aufloesung schon gezeigt wurde — ueberlebt ein Neuzeichnen. */
  private revealed = false;
  private beats: Beat[] = [];
  private timers: Phaser.Time.TimerEvent[] = [];
  /** Die hochzaehlende Bilanz, damit „alles zeigen“ sie sofort fertig setzen kann. */
  private tally: { text: Phaser.GameObjects.Text; tween: Phaser.Tweens.Tween | null } | null = null;

  constructor() {
    super('Finale');
  }

  init(): void {
    this.choice = null;
    this.verdict = null;
    this.scroll = null;
    this.revealed = false;
    this.beats = [];
    this.timers = [];
    this.tally = null;
  }

  create(): void {
    this.campaign = this.registry.get('campaign') as Campaign;
    this.cameras.main.setBackgroundColor(PALETTE.abyss);
    audio.startDrone(DRONE_ROOT.finale);
    audio.setTension(0.6);
    this.redraw();
    watchResize(this, () => this.redraw());
    this.cameras.main.fadeIn(motionMs(600), 5, 8, 13);

    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (this.verdict !== null && (event.key === ' ' || event.key === 'Enter')) this.showAll();
    });
  }

  private get forced(): boolean {
    return this.registry.get('finaleForced') === true;
  }

  /**
   * Beide Phasen liegen in einem Scrollbereich zwischen Titel und Knopf —
   * acht Aufloesungen mit grosser Schrift oder die Offenbarung auf einem
   * Telefon passen sonst nicht auf einen Bildschirm.
   */
  private redraw(): void {
    for (const timer of this.timers) timer.remove();
    this.timers = [];
    this.beats = [];
    this.tally = null;
    this.scroll?.destroy();
    this.scroll = null;
    this.children.removeAll(true);

    // Morgengrauen ueber offener See. Es ist das Letzte, was man von diesem
    // Fall sieht — und das einzige Bild im Spiel, das nicht duester ist.
    const backdrop = addBackdrop(this, FINALE_BACKDROP, this.verdict === null ? 0.16 : 0.24);
    if (backdrop !== null && this.verdict !== null && !getReducedMotion()) {
      // Langsam heranfahren, solange die Aufloesung laeuft.
      this.tweens.add({ targets: backdrop, scale: backdrop.scale * 1.06, duration: 22000, ease: 'Sine.easeOut' });
    }

    const layout = layoutOf(this);
    const { width, height } = layout;
    const centreX = width / 2;
    const panelWidth = Math.min(640, width - layout.margin * 2);
    const left = centreX - panelWidth / 2;

    const title = this.add
      .text(centreX, 20, t(this.verdict === null ? 'finale.hearing' : 'finale.title'), titleStyle(24))
      .setOrigin(0.5, 0);
    const top = title.y + title.height + 12;
    const footer = 84;

    this.scroll = createScrollArea(this, {
      x: left,
      y: top,
      width: panelWidth,
      height: height - top - footer,
    });

    if (this.verdict === null) {
      this.renderChoice(centreX, panelWidth, height);
      this.scroll.finish();
    } else {
      this.renderReveal(centreX, panelWidth, height, this.verdict);
    }
  }

  /** Setzt ein Objekt in den Scrollbereich und liefert seine Unterkante. */
  private place(object: Phaser.GameObjects.Text | Phaser.GameObjects.Container, gap = 10): number {
    this.scroll?.content.add(object);
    const bounds = object as unknown as { y: number; height: number };
    return bounds.y + bounds.height + gap;
  }

  // ------------------------------------------------------------------ Wahl

  private renderChoice(centreX: number, panelWidth: number, height: number): void {
    const mid = panelWidth / 2;
    const complete = this.campaign.isReadyForFinale;

    /*
     * Was der Ausschuss weiss.
     *
     * Erzwungen (Frist abgelaufen), vorzeitig (Akte unvollstaendig) oder
     * regulaer — der Satz darueber ist jedes Mal ein anderer, weil die Lage
     * eine andere ist. Wer vorzeitig urteilt, soll wissen, worauf er sich
     * einlaesst.
     */
    const situation = this.forced ? 'finale.situation.forced' : complete ? 'finale.situation.complete' : 'finale.situation.early';
    let y = this.place(
      this.add
        .text(mid, 0, t(situation, { collected: this.campaign.ledger.size, total: this.campaign.cluesPossible }), {
          ...readoutStyle(12),
          color: complete ? PALETTE.inkDim : TEXT_COLOUR.landmark,
          fontStyle: 'italic',
          wordWrap: { width: panelWidth },
          align: 'center',
        })
        .setOrigin(0.5, 0),
      12,
    );

    y = this.place(
      this.add
        .text(mid, y, t('finale.prompt'), {
          ...readoutStyle(13),
          wordWrap: { width: panelWidth },
          align: 'center',
        })
        .setOrigin(0.5, 0),
      16,
    );

    // Bewusst ohne Fundzahlen je Aufloesung: Das waere dieselbe Wertung, die
    // aus der Beweislage entfernt wurde, nur durch die Hintertuer.
    for (const id of MYSTERY_IDS) {
      const row = createListRow(this, 0, y, {
        width: panelWidth,
        height: 44,
        title: t(`mystery.${id}`),
        detail: t(`mystery.${id}.tagline`),
        selected: this.choice === id,
        onClick: () => {
          this.choice = id;
          this.redraw();
        },
      });
      y = this.place(row, 8);
    }

    if (this.choice !== null) {
      createButton(this, centreX, height - 44, t('finale.submit'), () => this.submit(), { width: 260 });
    } else if (!this.forced) {
      // Ohne Frist darf man noch einmal zurueck an die Karte.
      createButton(this, centreX, height - 44, t('finale.back'), () => this.scene.start('WorldMap'), {
        width: 220,
        fontSize: 12,
      });
    }
  }

  private submit(): void {
    if (this.choice === null) return;
    this.verdict = this.campaign.accuse(this.choice);
    // Der Durchlauf ist zu Ende — der Spielstand darf nicht bestehen bleiben,
    // sonst liesse sich das Urteil durch Neuladen zuruecknehmen.
    clearSave();
    audio.stopSonarSweep();
    audio.setTension(0.9);
    this.cameras.main.fadeOut(motionMs(500), 5, 8, 13);
    this.cameras.main.once(Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE, () => {
      this.redraw();
      this.cameras.main.fadeIn(motionMs(900), 5, 8, 13);
    });
    if (getReducedMotion()) this.redraw();
  }

  // -------------------------------------------------------------- Aufloesung

  /**
   * Die Aufloesung in Etappen.
   *
   * Jede Zeile bekommt einen Zeitpunkt. Sie entstehen alle sofort (damit die
   * Hoehen feststehen und gescrollt werden kann), aber unsichtbar, und werden
   * nacheinander eingeblendet. Laeuft die Aufloesung ueber den Bildschirm
   * hinaus, folgt der Scrollbereich der jeweils neuesten Zeile.
   */
  private renderReveal(centreX: number, panelWidth: number, height: number, verdict: Verdict): void {
    const mid = panelWidth / 2;
    const resolved = verdict.correct;
    const text = (
      content: string,
      style: Phaser.Types.GameObjects.Text.TextStyle,
      at: number,
      gap = 10,
      onShow?: () => void,
    ): number => {
      const object = this.add
        .text(mid, y, content, { align: 'center', wordWrap: { width: panelWidth }, ...style })
        .setOrigin(0.5, 0);
      this.beats.push({ object, at, ...(onShow === undefined ? {} : { onShow }) });
      return this.place(object, gap);
    };

    let y = 0;

    // 1. Ort und Zeit — wie der Kopf eines Protokolls.
    y = text(
      t('finale.dateline', { day: verdict.days }),
      { ...labelStyle(11, PALETTE.inkDim) },
      0.4,
      18,
    );

    // 2. Die Mannschaft reagiert, bevor die Aufloesung faellt.
    y = text(
      verdict.supported
        ? t('finale.crew.agree', { name: t('crew.first_officer') })
        : t('finale.crew.doubt', { name: t('crew.first_officer') }),
      { ...readoutStyle(12), color: verdict.supported ? PALETTE.inkDim : TEXT_COLOUR.landmark },
      1.6,
      18,
    );

    // 3. Das Urteil — mit dem Einschlag.
    y = text(
      resolved ? t('finale.correct') : t('finale.wrong'),
      {
        fontFamily: FONT.display,
        fontSize: px(34),
        color: resolved ? TEXT_COLOUR.base : TEXT_COLOUR.danger,
      },
      3.6,
      8,
      () => audio.impact(0.32),
    );

    // 4. Die Wahrheit im Wortlaut.
    y = text(
      t('finale.truth', { truth: t(`mystery.${verdict.truth}`) }),
      { ...readoutStyle(14) },
      5.2,
      12,
    );
    const revelation = this.add.text(0, y, t(`mystery.${verdict.truth}.revelation`), {
      ...readoutStyle(13),
      wordWrap: { width: panelWidth },
      lineSpacing: 4,
    });
    this.beats.push({ object: revelation, at: 6.4 });
    y = this.place(revelation, 22);

    // 5. Der Preis — die Zahlen zaehlen hoch.
    const rule = this.add.rectangle(0, y, panelWidth, 1, PALETTE.panelEdge).setOrigin(0, 0);
    this.scroll?.content.add(rule);
    this.beats.push({ object: rule as unknown as Beat['object'], at: 9.4 });
    y += 12;
    y = text(t('toll.title'), { ...labelStyle(12, TEXT_COLOUR.coast) }, 9.6, 6);

    const tally = this.add
      .text(mid, y, t('finale.tally', { rescued: 0, lost: 0 }), {
        ...readoutStyle(14),
        color: verdict.lost > verdict.rescued ? TEXT_COLOUR.danger : PALETTE.ink,
        align: 'center',
        wordWrap: { width: panelWidth },
      })
      .setOrigin(0.5, 0);
    this.tally = { text: tally, tween: null };
    this.beats.push({
      object: tally,
      at: 10.0,
      onShow: () => this.countUp(verdict.rescued, verdict.lost),
    });
    y = this.place(tally, 6);

    const toll = this.campaign.toll;
    if (toll.avoidableLost > 0) {
      y = text(
        t('toll.avoidable', { count: toll.avoidableLost }),
        { ...readoutStyle(11), color: PALETTE.inkDim },
        11.6,
        6,
      );
    }
    const hard = this.campaign.ledger.all.filter((clue) =>
      (HARD_SLOTS as readonly ClueSlot[]).includes(clue.slot),
    ).length;
    y = text(
      t(verdictKey(toll, hard)),
      { ...readoutStyle(12), color: PALETTE.inkDim, fontStyle: 'italic', lineSpacing: 3 },
      12.4,
      20,
    );

    // 6. Die persoenliche Antwort — oder ihr Fehlen.
    y = text(
      verdict.personalAnswered
        ? t('finale.personal.found', { name: this.campaign.character.lostName })
        : t('finale.personal.open', { name: this.campaign.character.lostName }),
      { ...readoutStyle(12), color: PALETTE.inkDim, fontStyle: 'italic' },
      14.2,
      26,
    );

    // 7. Das Ende: Titel und Epilog, je nach Kombination aus richtig und belegt.
    y = text(
      t(`finale.ending.${verdict.ending}.title`),
      { fontFamily: FONT.display, fontSize: px(26), color: TEXT_COLOUR.coast },
      16.4,
      10,
      () => {
        audio.impact(0.2);
        audio.resolution(resolved);
      },
    );
    y = text(
      t(`finale.ending.${verdict.ending}.body`),
      { ...readoutStyle(13), lineSpacing: 4 },
      17.6,
      24,
    );

    // 8. Die Schlussbilanz in einer Zeile, wie ein Abspann.
    y = text(
      [
        this.campaign.character.name,
        t('finale.stats.days', { count: verdict.days }),
        t('finale.stats.missions', { count: verdict.missions }),
        t('finale.collected', { collected: verdict.cluesCollected, total: verdict.cluesPossible }),
        t(`difficulty.${verdict.difficulty}`),
        t('common.seed', { seed: this.campaign.seed }),
      ].join('  ·  '),
      { fontFamily: FONT.mono, fontSize: px(10), color: PALETTE.inkFaint },
      19.4,
      8,
    );

    this.scroll?.finish();

    const restart = createButton(this, centreX, height - 44, t('finale.restart'), () => {
      this.registry.remove('campaign');
      this.registry.remove('finaleForced');
      this.scene.start('Menu');
    }, { width: 240 });

    // Tippen irgendwo zeigt alles auf einmal.
    const skipHint = this.add
      .text(centreX, height - 44, t('finale.skip'), { ...labelStyle(10, PALETTE.inkFaint) })
      .setOrigin(0.5);

    if (this.revealed || getReducedMotion()) {
      this.showAll();
      skipHint.destroy();
      return;
    }

    restart.setVisible(false);
    for (const beat of this.beats) beat.object.alpha = 0;
    const last = Math.max(...this.beats.map((beat) => beat.at));

    for (const beat of this.beats) {
      this.timers.push(
        this.time.delayedCall(beat.at * 1000, () => {
          this.tweens.add({ targets: beat.object, alpha: 1, duration: 900, ease: 'Sine.easeOut' });
          beat.onShow?.();
          this.follow(beat);
        }),
      );
    }
    this.timers.push(
      this.time.delayedCall((last + 1.2) * 1000, () => {
        this.revealed = true;
        skipHint.destroy();
        restart.setVisible(true);
      }),
    );

    this.input.once('pointerdown', () => {
      if (this.revealed) return;
      skipHint.destroy();
      restart.setVisible(true);
      this.showAll();
    });
  }

  /** Haelt die neueste Zeile im Bild, wenn die Aufloesung laenger ist als der Schirm. */
  private follow(beat: Beat): void {
    const area = this.scroll;
    if (area === null) return;
    const bottom = beat.object.y + (beat.object.height ?? 0);
    const view = this.scale.height - 140;
    if (bottom > area.offset() + view) area.scrollTo(bottom - view + 20);
  }

  /** Alles sofort — fuer Ungeduldige, reduzierte Bewegung und Neuzeichnen. */
  private showAll(): void {
    if (this.revealed && this.timers.length === 0) return;
    for (const timer of this.timers) timer.remove();
    this.timers = [];
    this.revealed = true;
    for (const beat of this.beats) {
      this.tweens.killTweensOf(beat.object);
      beat.object.alpha = 1;
    }
    const verdict = this.verdict;
    if (verdict !== null && this.tally !== null) {
      this.tally.tween?.stop();
      this.tally.text.setText(t('finale.tally', { rescued: verdict.rescued, lost: verdict.lost }));
    }
  }

  /** Die Bilanz zaehlt hoch, statt einfach dazustehen. */
  private countUp(rescued: number, lost: number): void {
    const target = this.tally?.text;
    if (target === undefined || this.tally === null) return;
    const state = { k: 0 };
    this.tally.tween = this.tweens.add({
      targets: state,
      k: 1,
      duration: 1600,
      ease: 'Cubic.easeOut',
      onUpdate: () =>
        target.setText(
          t('finale.tally', { rescued: Math.round(rescued * state.k), lost: Math.round(lost * state.k) }),
        ),
    });
  }
}
